import axios from "axios";
import {
  ASSIGN_UPDATE_TEMPLATE,
  LIST_CONTRACTS_FOR_ADDENDUM,
  LIST_STATUS_TEMPLATE,
  UPDATE_TEMPLATE,
  UPLOAD_TEMPLATE_URL,
} from "@/utils/constants/constants.js";
import { validate_file, validate_non_empty_string } from "@/utils/validations/validate_inputs";

export default {
  methods: {
    select_template(template, action = "update") {
      if (!template) return;

      const source = this.manage_template.find(item => item.id === template.id) || template;
      this.selected_template = { ...source };

      if (action === "view") {
        this.preview_modal = true;
        return;
      }

      this.preview_modal = false;
    },
    configure_template(template_entity_type_id) {
      if (!template_entity_type_id) return;
      this.$router.push({
        path: "/configure_template",
        query: {
          template_entity_type_id,
        },
      });
    },
    async handle_file_selection(evt) {
      const file = evt.target.files[0];
      if (!file) {
        this.selected_file = null;
        return;
      }

      try {
        const validation = await validate_file(file);
        if (!validation.valid) {
          this.show_error(validation.error);
          evt.target.value = "";
          this.selected_file = null;
          return;
        }
        this.selected_file = file;
      } catch {
        this.show_error("Error validating file");
        evt.target.value = "";
        this.selected_file = null;
      }
    },
    validate_template_name_input() {
      if (!this.new_template_name) {
        this.show_error("Template name is required.");
        return false;
      }
      if (!validate_non_empty_string(this.new_template_name)) {
        this.show_error("Template name must not be empty");
        return false;
      }
      return true;
    },
    async upload_and_assign_template() {
      if (!this.validate_template_name_input()) return;
      if (!this.selected_file) {
        this.show_error("Please select a file to upload");
        return;
      }

      this.upload_loading = true;
      const formData = new FormData();
      formData.append("name", this.new_template_name);
      formData.append("project_id", this.project_id);
      formData.append("document_file", this.selected_file);

      try {
        const uploadResponse = await axios.post(UPLOAD_TEMPLATE_URL, formData, {
          headers: { ...this.headers, "Content-Type": "multipart/form-data" },
        });

        if (uploadResponse.data?.status !== "success") {
          throw new Error(uploadResponse.data?.message || "Template upload failed");
        }

        const assignResponse = await axios.post(
          ASSIGN_UPDATE_TEMPLATE,
          {
            template_id: uploadResponse.data.data.id,
            entity_type_id: this.entity_type_id,
            is_assigned: true,
          },
          { headers: this.headers }
        );

        if (assignResponse.data?.status === "success") {
          this.show_success("Template uploaded and assigned successfully");
          await this.get_assigned_templates();
          this.close_upload_modal();
        } else {
          this.show_error("Template uploaded but assignment failed");
        }
      } catch (error) {
        this.show_error(error.response?.data?.message || error.message || "Error during template upload and assignment");
      } finally {
        this.upload_loading = false;
      }
    },
    open_upload_modal() {
      this.show_upload_modal = true;
      this.new_template_name = "";
      this.selected_file = null;
      const fileInput = document.querySelector("#template-upload-input");
      if (fileInput) fileInput.value = "";
    },
    close_upload_modal() {
      this.show_upload_modal = false;
    },
    async update_template() {
      if (!this.validate_template_name_input()) return;

      this.loading = true;
      const form_data = new FormData();
      form_data.append("template_id", this.selected_template.id);
      if (this.selected_file) form_data.append("new_document_file", this.selected_file);
      form_data.append("new_name", this.selected_template.template_name);
      form_data.append("new_project_id", this.project_id);

      try {
        const response = await axios.post(UPDATE_TEMPLATE, form_data, {
          headers: { ...this.headers, "Content-Type": "multipart/form-data" },
        });

        if (response.data?.status !== "success") {
          throw new Error(response.data?.message || "Failed to update template");
        }

        this.show_success("Template updated successfully.");
        await this.get_assigned_templates();
      } catch (error) {
        this.show_error(error.response?.data?.message || error.message || "Update failed");
      } finally {
        this.loading = false;
      }
    },
    async get_assigned_templates() {
      this.loading_templates = true;
      try {
        const response = await axios.get(LIST_STATUS_TEMPLATE, {
          headers: this.headers,
          params: { project_id: this.project_id, entity_type_id: this.entity_type_id },
        });

        const raw = response.data?.data;
        const templates_data = Array.isArray(raw)
          ? raw
          : Array.isArray(raw?.active_assignments)
            ? raw.active_assignments
            : [];

        this.manage_template = templates_data.map(assignment => {
          const uploaded_raw = assignment.created_at || new Date().toISOString();
          const modified_raw = assignment.updated_at || uploaded_raw;
          return {
            id: assignment.id,
            entity_type_id: assignment.entity_type_id || null,
            template_name: assignment.template_name || "Unnamed Template",
            file_type: assignment.document_url
              ? assignment.document_url.slice(assignment.document_url.lastIndexOf(".") + 1)
              : "Unknown File Type",
            date_uploaded: new Date(uploaded_raw).toLocaleString(),
            date_modified: new Date(modified_raw).toLocaleString(),
            document_url: assignment.document_url || null,
            presigned_url: assignment.presigned_url || null,
            version: assignment.version || 1,
            entity_type_name: assignment.entity_type_name,
            template_document_id: assignment.template_document,
            template_type: assignment.template_type?.toLowerCase() || "template",
            uploaded_by: assignment.uploaded_by || "N/A",
            linked_contracts: [],
          };
        });

        for (const template of this.manage_template) {
          if (template.template_type === "annexure" || template.template_type === "addendum") {
            await this.fetch_linked_contracts(template.id);
          }
        }
      } catch (error) {
        this.show_error("Error fetching templates: " + (error.response?.data?.message || error.message));
        this.manage_template = [];
      } finally {
        this.loading_templates = false;
      }
    },
    async fetch_linked_contracts(addendum_id) {
      try {
        const response = await axios.get(LIST_CONTRACTS_FOR_ADDENDUM, {
          headers: this.headers,
          params: { addendum_entity_type_id: addendum_id },
        });

        if (response.data?.status !== "success") return;
        const addendum = this.manage_template.find(t => t.id === addendum_id);
        if (!addendum) return;

        addendum.linked_contracts = (response.data.data || []).map(contract => ({
          id: contract.contract_entity_type_id,
          template_name: contract.contract_name,
          template_type: "contract",
          date_uploaded: contract.created_at ? new Date(contract.created_at).toLocaleString() : "",
          date_modified: contract.updated_at ? new Date(contract.updated_at).toLocaleString() : "",
        }));
      } catch {
        this.show_error("Error fetching linked contracts");
      }
    },
    get_linked_contracts(addendum_id) {
      const template = this.manage_template.find(t => t.id === addendum_id);
      return template?.linked_contracts || [];
    },
    toggle_template_expansion(template_id) {
      const index = this.expanded_templates.indexOf(template_id);
      if (index === -1) {
        this.expanded_templates.push(template_id);
      } else {
        this.expanded_templates.splice(index, 1);
      }
    },
    is_template_expanded(template_id) {
      return this.expanded_templates.includes(template_id);
    },
    close_the_file_preview() {
      this.preview_modal = false;
    },
    is_expandable(template) {
      const type = template.template_type?.toLowerCase();
      return type === "annexure" || type === "addendum";
    },
  },
};
